import { useContext } from 'react';
import { Nav, Navbar } from 'react-bootstrap';
import { useHistory } from 'react-router';
import type { DefaultTheme } from 'styled-components';
import styled from 'styled-components';

import { AppContext } from '../context/AppContext';
import { signOut } from '../utils/auth';
import { max } from '../utils/breakpoints';

import AnimatedToggle from './AnimatedToggle';
import ThemeAwareLogo from './ThemeAwareLogo';

interface StyledProps {
  theme: DefaultTheme;
  $isLoginScreen?: boolean;
}

// Props
interface MainNavProps {
  isLoginScreen?: boolean;
  showSignOut?: boolean;
  isAdmin?: boolean;
  isOnboarding?: boolean;
  user?: unknown;
}

// Styled components
const StyledContainer = styled.div<StyledProps>`
  display: flex;
  align-items: center;
  justify-content: ${({ $isLoginScreen, theme }) =>
    $isLoginScreen ? 'center' : theme.navbar.container['${min.md}']?.justifyContent || 'space-between'};
  flex-wrap: nowrap;
  width: 100%;
`;

const StyledNavbarToggle = styled(Navbar.Toggle)`
  border: none;
`;

const StyledNavbarBrand = styled(Navbar.Brand)`
  padding-left: ${({ $isLoginScreen }) => ($isLoginScreen ? '0' : '22px')};
  padding-top: 0;
  flex-shrink: 1;

  ${max.sm} {
    img {
      width: 95%;
      max-height: 80px;
    }
  }
`;

const StyledDesktopSignOutNav = styled(Nav)`
  display: block;
  position: absolute;
  right: 30px;
  top: 32px;
  ${max.lg} {
    display: none;
  }
`;

// MainNav component
const MainNav = ({
  isLoginScreen = false,
  showSignOut = false,
  isAdmin = false,
  isOnboarding = false,
  user = null,
}: MainNavProps) => {
  const history = useHistory();
  const context = useContext(AppContext);

  // Provide default value when context is undefined (e.g., during authentication flow)
  const showTariffRecommender = context?.showTariffRecommender ?? false;

  return (
    <>
      <StyledContainer $isLoginScreen={isLoginScreen}>
        <div className="theme-logo">
          <StyledNavbarBrand href="/" $isLoginScreen={isLoginScreen}>
            <ThemeAwareLogo stacked={true} className="d-block d-sm-none mobile-logo" alt="Company Logo" />
            <ThemeAwareLogo className="d-none d-sm-block" alt="Company Logo" />
          </StyledNavbarBrand>
        </div>
        {!isLoginScreen && <StyledNavbarToggle aria-controls="responsive-navbar-nav" children={AnimatedToggle} />}
      </StyledContainer>
      {/* @ts-ignore */}
      <Navbar.Collapse id="responsive-navbar-nav" className="pt-3 pl-4">
        {!isLoginScreen && !showSignOut && !isOnboarding && (
          <Nav className="ml-auto pb-4 d-lg-none d-md-block">
            <Nav.Link href="/app/today">Today</Nav.Link>
            {context.showSavings && <Nav.Link href="/app/savings">Savings</Nav.Link>}
            <Nav.Link href="/app/energy-flow">Energy Flow</Nav.Link>
            {showTariffRecommender && <Nav.Link href="/app/tariffs">Tariff Recommender</Nav.Link>}
            <Nav.Link href="/app/settings">Settings</Nav.Link>
            <Nav.Link href="/app/help">Help</Nav.Link>
          </Nav>
        )}
        {!isLoginScreen && showSignOut && (
          <Nav className="ml-auto pb-4 d-lg-none d-md-block">
            <Nav.Link href="#" onClick={() => signOut()}>
              Sign Out
            </Nav.Link>
          </Nav>
        )}
      </Navbar.Collapse>
      {showSignOut && (
        <StyledDesktopSignOutNav className="ml-auto">
          {!!user && !isAdmin && showSignOut && (
            <Nav.Link href="#" onClick={() => signOut()}>
              Sign Out
            </Nav.Link>
          )}
        </StyledDesktopSignOutNav>
      )}
    </>
  );
};

export default MainNav;
